import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Twitter,
  Sparkles,
  Loader2,
  FileText,
  CheckCircle2,
  Copy,
  ChevronDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useGenerateArticle, type GenerateArticleResponse } from "@/hooks/use-email";
import { toast } from "sonner";

interface TweetToArticleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsertArticle: (title: string, content: string) => void;
}

type GenerationStep = "input" | "generating" | "preview";

export function TweetToArticleModal({
  open,
  onOpenChange,
  onInsertArticle,
}: TweetToArticleModalProps) {
  const [step, setStep] = useState<GenerationStep>("input");
  const [tweetText, setTweetText] = useState("");
  const [tweetUrl, setTweetUrl] = useState("");
  const [targetLength, setTargetLength] = useState<string>("1200");
  const [style, setStyle] = useState<"informative" | "conversational" | "analytical">("conversational");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState<GenerateArticleResponse["article"] | null>(null);

  const generateArticle = useGenerateArticle();

  const handleGenerate = async () => {
    if (!tweetText.trim()) {
      toast.error("Please paste your tweet content");
      return;
    }

    setStep("generating");

    try {
      const result = await generateArticle.mutateAsync({
        tweetText: tweetText.trim(),
        tweetUrl: tweetUrl.trim() || undefined,
        targetLength: parseInt(targetLength, 10),
        style,
      });

      if (result.success && result.article) {
        setGeneratedArticle(result.article);
        setStep("preview");
        toast.success(`Article generated: ${result.article.wordCount} words`);
      } else {
        throw new Error(result.error || "Failed to generate article");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to generate article");
      setStep("input");
    }
  };

  const handleInsert = () => {
    if (generatedArticle) {
      onInsertArticle(generatedArticle.title, generatedArticle.content);
      handleClose();
      toast.success("Article inserted into editor");
    }
  };

  const handleCopyToClipboard = async () => {
    if (generatedArticle) {
      await navigator.clipboard.writeText(generatedArticle.content);
      toast.success("Article copied to clipboard");
    }
  };

  const handleClose = () => {
    setStep("input");
    setTweetText("");
    setTweetUrl("");
    setGeneratedArticle(null);
    onOpenChange(false);
  };

  const handleStartOver = () => {
    setStep("input");
    setGeneratedArticle(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-sky-500/10">
              <Twitter className="w-5 h-5 text-sky-500" />
            </div>
            Generate Article from Tweet
          </DialogTitle>
          <DialogDescription>
            Transform your viral tweets into full-length newsletter articles using AI.
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {/* Input Step */}
          {step === "input" && (
            <motion.div
              key="input"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4 flex-1 overflow-y-auto"
            >
              <div className="space-y-2">
                <Label htmlFor="tweet-text" className="text-sm font-medium">
                  Tweet Content <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="tweet-text"
                  value={tweetText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTweetText(e.target.value)}
                  placeholder="Paste your tweet text here...&#10;&#10;For example: 'Just realized the biggest mistake founders make is building features nobody asked for. The best products solve problems people are already paying to fix. Thread...'"
                  className="min-h-[120px] resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  {tweetText.length} characters
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tweet-url" className="text-sm font-medium">
                  Tweet URL (optional)
                </Label>
                <Input
                  id="tweet-url"
                  value={tweetUrl}
                  onChange={(e) => setTweetUrl(e.target.value)}
                  placeholder="https://twitter.com/user/status/..."
                  type="url"
                />
                <p className="text-xs text-muted-foreground">
                  Include the tweet URL for reference and potential embedding
                </p>
              </div>

              {/* Advanced Options */}
              <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 text-muted-foreground hover:text-foreground"
                  >
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 transition-transform",
                        showAdvanced && "rotate-180"
                      )}
                    />
                    Advanced Options
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Target Word Count</Label>
                      <Select value={targetLength} onValueChange={setTargetLength}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="800">~800 words (Short)</SelectItem>
                          <SelectItem value="1200">~1,200 words (Standard)</SelectItem>
                          <SelectItem value="1800">~1,800 words (Long)</SelectItem>
                          <SelectItem value="2500">~2,500 words (Deep Dive)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Writing Style</Label>
                      <Select value={style} onValueChange={(v) => setStyle(v as typeof style)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="conversational">Conversational</SelectItem>
                          <SelectItem value="informative">Informative</SelectItem>
                          <SelectItem value="analytical">Analytical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Tips Card */}
              <Card className="p-4 bg-amber-500/5 border-amber-500/20">
                <div className="flex gap-3">
                  <Sparkles className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                      Tips for Best Results
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>- Include the full tweet/thread content for context</li>
                      <li>- Tweets with strong opinions or insights work best</li>
                      <li>- The AI will expand on your ideas while maintaining your voice</li>
                    </ul>
                  </div>
                </div>
              </Card>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={!tweetText.trim()}
                  className="gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate Article
                </Button>
              </div>
            </motion.div>
          )}

          {/* Generating Step */}
          {step === "generating" && (
            <motion.div
              key="generating"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="flex-1 flex flex-col items-center justify-center py-12 space-y-6"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="p-4 rounded-full bg-primary/10"
              >
                <Sparkles className="w-8 h-8 text-primary" />
              </motion.div>

              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold">Generating Your Article</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Our AI is expanding your tweet into a full newsletter article.
                  This typically takes 15-30 seconds.
                </p>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Analyzing content and generating...</span>
              </div>

              {/* Progress steps */}
              <div className="flex gap-8 pt-4">
                {["Analyzing", "Writing", "Polishing"].map((label, index) => (
                  <motion.div
                    key={label}
                    initial={{ opacity: 0.3 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 5 }}
                    className="flex flex-col items-center gap-2"
                  >
                    <motion.div
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 5, duration: 0.3 }}
                      className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center",
                        "bg-muted text-muted-foreground"
                      )}
                    >
                      {index + 1}
                    </motion.div>
                    <span className="text-xs text-muted-foreground">{label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Preview Step */}
          {step === "preview" && generatedArticle && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex-1 flex flex-col min-h-0"
            >
              {/* Success Banner */}
              <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20 mb-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                    Article Generated Successfully
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {generatedArticle.wordCount.toLocaleString()} words generated
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyToClipboard}
                  className="gap-1.5 shrink-0"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </Button>
              </div>

              {/* Article Preview */}
              <div className="flex-1 overflow-y-auto min-h-0 border rounded-lg bg-card">
                <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b px-4 py-3">
                  <h3 className="font-semibold line-clamp-1">{generatedArticle.title}</h3>
                </div>
                <div
                  className="p-4 prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: generatedArticle.content }}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center gap-3 pt-4 border-t mt-4">
                <Button
                  variant="ghost"
                  onClick={handleStartOver}
                  className="gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Start Over
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button onClick={handleInsert} className="gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Insert into Email
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
